import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Me, Stage, TeamMember } from '@shared/types';
import type { RateTable } from '@shared/currency';
import { convertWith } from '@shared/currency';
import { PROFILE_TERMS_KEY, type ProfileTerms } from '@shared/profiles';
import { api } from './api';

// Data almost every screen needs: who you are, the team, the deal stages, exchange rates and the company profile
// vocabulary.

export const meQuery = { queryKey: ['me'], queryFn: () => api.get<Me>('/auth/me'), staleTime: 60_000 } as const;

/** The signed-in person and workspace. Only use inside the signed-in app, where it has always loaded. */
export function useMe(): Me {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error('useMe() used before sign-in finished');
  return data;
}

export function useIsAdmin(): boolean {
  return useMe().user.role === 'super_admin';
}

export function useTeam(): { members: TeamMember[]; byId: Map<string, TeamMember>; active: TeamMember[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({ queryKey: ['team'], queryFn: () => api.get<TeamMember[]>('/team'), staleTime: 5 * 60_000 });
  return useMemo(() => {
    const members = data ?? [];
    return {
      members,
      byId: new Map(members.map((m) => [m.id, m])),
      active: members.filter((m) => m.status === 'active'),
      isLoading,
    };
  }, [data, isLoading]);
}

export function useStages(): { stages: Stage[]; byId: Map<string, Stage>; isLoading: boolean } {
  const { data, isLoading } = useQuery({ queryKey: ['stages'], queryFn: () => api.get<Stage[]>('/stages'), staleTime: 5 * 60_000 });
  return useMemo(() => {
    const stages = data ?? [];
    return { stages, byId: new Map(stages.map((s) => [s.id, s])), isLoading };
  }, [data, isLoading]);
}

/** Exchange rates plus a converter into the workspace's main currency. */
export function useRates(): { rates: RateTable | undefined; toMain: (amount: number, currency: string) => number; mainCurrency: string } {
  const me = useMe();
  const { data } = useQuery({ queryKey: ['rates'], queryFn: () => api.get<RateTable>('/currency/rates'), staleTime: 30 * 60_000 });
  const mainCurrency = me.workspace.mainCurrency;
  return useMemo(
    () => ({
      rates: data,
      mainCurrency,
      toMain: (amount: number, currency: string) => (data ? convertWith(data.rates, amount, currency, mainCurrency) : amount),
    }),
    [data, mainCurrency],
  );
}

/** What the team offers, and the capabilities and markets companies use. */
export function useProfileTerms(enabled = true) {
  return useQuery({
    queryKey: [...PROFILE_TERMS_KEY],
    queryFn: ({ signal }) => api.get<ProfileTerms>('/profile-terms', { signal }),
    staleTime: 60_000,
    enabled,
  });
}

export function useDebounced<T>(value: T, ms = 160): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** True when the person has asked their system for less motion. */
export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Keeps something mounted while it animates out. Returns [mounted, shown]: render while mounted, and put the
 * "show" state on while shown. Closing waits `ms` before unmounting (immediately with reduced motion).
 */
export function usePresence(open: boolean, ms = 300): { mounted: boolean; shown: boolean; closing: boolean } {
  const [state, setState] = useState({ mounted: open, shown: open, closing: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    clearTimeout(timer.current);
    if (open) {
      setState({ mounted: true, shown: false, closing: false });
      // Two frames so the closed state paints before the transition to shown starts.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setState({ mounted: true, shown: true, closing: false }));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    if (prefersReducedMotion()) {
      setState({ mounted: false, shown: false, closing: false });
      return;
    }
    setState((s) => (s.mounted ? { mounted: true, shown: false, closing: true } : s));
    timer.current = setTimeout(() => setState({ mounted: false, shown: false, closing: false }), ms);
    return () => clearTimeout(timer.current);
  }, [open, ms]);
  return state;
}
